/// <reference types="vite/client" />

const API_URL = import.meta.env.VITE_API_URL || "";

// Token refresh configuration
const TOKEN_REFRESH_BUFFER_SECONDS = 60; // Refresh 60 seconds before expiry

interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  skipAuth?: boolean; // For endpoints that don't need auth (login, refresh)
}

interface TokenData {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  expiresAt: number; // Timestamp when token expires
}

class ApiClient {
  private tokenData: TokenData | null = null;
  private refreshPromise: Promise<void> | null = null;
  private onTokenRefresh: ((tokens: TokenData) => void) | null = null;
  private onAuthError: (() => void) | null = null;

  setTokens(
    data: {
      accessToken: string;
      refreshToken: string;
      expiresIn: number;
    } | null
  ) {
    if (data) {
      this.tokenData = {
        ...data,
        expiresAt: Date.now() + data.expiresIn * 1000,
      };
    } else {
      this.tokenData = null;
    }
  }

  getTokenData(): TokenData | null {
    return this.tokenData;
  }

  // For backwards compatibility
  setToken(token: string | null) {
    if (token && this.tokenData) {
      this.tokenData.accessToken = token;
    } else if (!token) {
      this.tokenData = null;
    }
  }

  // Set callback for when tokens are refreshed
  onTokensRefreshed(callback: (tokens: TokenData) => void) {
    this.onTokenRefresh = callback;
  }

  // Set callback for auth errors (token refresh failed)
  onAuthenticationError(callback: () => void) {
    this.onAuthError = callback;
  }

  private isTokenExpiringSoon(): boolean {
    if (!this.tokenData) return true;
    const bufferMs = TOKEN_REFRESH_BUFFER_SECONDS * 1000;
    return Date.now() >= this.tokenData.expiresAt - bufferMs;
  }

  private async refreshTokens(): Promise<void> {
    if (!this.tokenData?.refreshToken) {
      throw new Error("No refresh token available");
    }

    // If already refreshing, wait for that promise
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken: this.tokenData!.refreshToken }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || "Token refresh failed");
        }

        this.setTokens(data.data);

        // Notify store of new tokens
        if (this.onTokenRefresh && this.tokenData) {
          this.onTokenRefresh(this.tokenData);
        }
      } catch (error) {
        // Clear tokens and notify of auth error
        this.tokenData = null;
        if (this.onAuthError) {
          this.onAuthError();
        }
        throw error;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    // Refresh token if needed (unless this is a skip-auth request)
    if (!options.skipAuth && this.tokenData && this.isTokenExpiringSoon()) {
      try {
        await this.refreshTokens();
      } catch {
        // If refresh fails, continue with the request - it might still work
        // or the 401 will trigger logout
      }
    }

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...options.headers,
    };

    if (!options.skipAuth && this.tokenData?.accessToken) {
      headers["Authorization"] = `Bearer ${this.tokenData.accessToken}`;
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      method: options.method || "GET",
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
    });

    const data = await response.json();

    if (!response.ok) {
      // If 401 and we have a refresh token, try refreshing once
      if (
        response.status === 401 &&
        this.tokenData?.refreshToken &&
        !options.skipAuth
      ) {
        try {
          await this.refreshTokens();
          // Retry the request with new token
          return this.request(endpoint, options);
        } catch {
          // Refresh failed, throw original error
        }
      }
      throw new Error(data.message || data.error || "Request failed");
    }

    return data;
  }

  // Auth
  async login(email: string, password: string) {
    const result = await this.request<{
      success: boolean;
      data: {
        user: any;
        accessToken: string;
        refreshToken: string;
        expiresIn: number;
      };
    }>("/api/auth/login", {
      method: "POST",
      body: { email, password },
      skipAuth: true,
    });

    // Set tokens from login response
    this.setTokens(result.data);

    return result;
  }

  async logout(refreshToken?: string) {
    try {
      await this.request("/api/auth/logout", {
        method: "POST",
        body: { refreshToken },
        skipAuth: true,
      });
    } finally {
      this.tokenData = null;
    }
  }

  async getMe() {
    return this.request<{ success: boolean; data: any }>("/api/auth/me");
  }

  // Places
  async getPlaces(
    params: Record<string, string | number | boolean | undefined> = {}
  ) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== "") {
        searchParams.append(key, String(value));
      }
    });
    return this.request<{ success: boolean; data: any }>(
      `/api/places?${searchParams.toString()}`
    );
  }

  async getPlace(id: string) {
    return this.request<{ success: boolean; data: any }>(`/api/places/${id}`);
  }

  async getPlaceRecommendations(
    id: string,
    options: { includeAI?: boolean; quick?: boolean } = {}
  ) {
    const searchParams = new URLSearchParams();
    if (options.includeAI !== undefined) {
      searchParams.append("includeAI", String(options.includeAI));
    }
    if (options.quick !== undefined) {
      searchParams.append("quick", String(options.quick));
    }
    const query = searchParams.toString();
    return this.request<{ success: boolean; data: any }>(
      `/api/places/${id}/recommendations${query ? `?${query}` : ""}`
    );
  }

  async getHeatmapData(
    params: Record<string, string | number | boolean | undefined> = {}
  ) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== "") {
        searchParams.append(key, String(value));
      }
    });
    return this.request<{ success: boolean; data: any[] }>(
      `/api/places/heatmap?${searchParams.toString()}`
    );
  }

  async getMapPlaces(
    params: Record<string, string | number | boolean | undefined> = {}
  ) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== "") {
        searchParams.append(key, String(value));
      }
    });
    return this.request<{
      success: boolean;
      data: Array<{
        _id: string;
        name: string;
        address: string;
        category: string;
        score: number;
        lat: number;
        lng: number;
      }>;
    }>(`/api/places/map?${searchParams.toString()}`);
  }

  async updateOutreach(placeId: string, data: any, planId?: string) {
    const params = planId ? `?planId=${planId}` : "";
    return this.request<{ success: boolean; data: any }>(
      `/api/places/${placeId}/outreach${params}`,
      {
        method: "POST",
        body: data,
      }
    );
  }

  // Plans
  async createPlan(data: any) {
    return this.request<{ success: boolean; data: any }>(
      "/api/outreach-plans",
      {
        method: "POST",
        body: data,
      }
    );
  }

  async getPlans() {
    return this.request<{ success: boolean; data: any[] }>(
      "/api/outreach-plans"
    );
  }

  async getPlan(id: string) {
    return this.request<{ success: boolean; data: any }>(
      `/api/outreach-plans/${id}`
    );
  }

  async deletePlan(id: string) {
    return this.request<{ success: boolean }>(`/api/outreach-plans/${id}`, {
      method: "DELETE",
    });
  }

  async getWeekPlaces(planId: string, week: number) {
    return this.request<{ success: boolean; data: any[] }>(
      `/api/outreach-plans/${planId}/week/${week}`
    );
  }

  // Reports
  async getReportOverview(planId?: string) {
    const params = planId ? `?planId=${planId}` : "";
    return this.request<{ success: boolean; data: any }>(
      `/api/reports/overview${params}`
    );
  }

  // Organizations
  async getMyOrganization() {
    return this.request<{ success: boolean; data: any }>(
      "/api/organizations/my"
    );
  }

  async getOrganizationMembers() {
    return this.request<{ success: boolean; data: any[] }>(
      "/api/organizations/my/members"
    );
  }

  async addOrganizationMember(
    email: string,
    orgRole: "admin" | "member" = "member"
  ) {
    return this.request<{ success: boolean; data: any; message?: string }>(
      "/api/organizations/my/members",
      {
        method: "POST",
        body: { email, orgRole },
      }
    );
  }

  async removeOrganizationMember(userId: string) {
    return this.request<{ success: boolean; message?: string }>(
      `/api/organizations/my/members/${userId}`,
      {
        method: "DELETE",
      }
    );
  }

  async updateMemberRole(
    userId: string,
    orgRole: "owner" | "admin" | "member"
  ) {
    return this.request<{ success: boolean; message?: string }>(
      `/api/organizations/my/members/${userId}/role`,
      {
        method: "PATCH",
        body: { orgRole },
      }
    );
  }

  async createOrganizationUser(
    email: string,
    name: string,
    orgRole: "admin" | "member" = "member"
  ) {
    return this.request<{
      success: boolean;
      data: { user: any; generatedPassword: string };
      message?: string;
    }>("/api/organizations/my/users", {
      method: "POST",
      body: { email, name, orgRole },
    });
  }

  async deleteOrganizationUser(userId: string) {
    return this.request<{ success: boolean; message?: string }>(
      `/api/organizations/my/users/${userId}`,
      {
        method: "DELETE",
      }
    );
  }

  async resetUserPassword(userId: string) {
    return this.request<{
      success: boolean;
      data: { newPassword: string };
      message?: string;
    }>(`/api/organizations/my/users/${userId}/reset-password`, {
      method: "POST",
    });
  }

  // Funding Opportunities
  async getFundingOpportunities(params?: { scope?: string; search?: string }) {
    const searchParams = new URLSearchParams();
    if (params?.scope) searchParams.append("scope", params.scope);
    if (params?.search) searchParams.append("search", params.search);
    const query = searchParams.toString() ? `?${searchParams.toString()}` : "";
    return this.request<{
      success: boolean;
      data: { opportunities: any[]; total: number };
    }>(`/api/funding/opportunities${query}`);
  }

  async getFundingOpportunity(id: string) {
    return this.request<{ success: boolean; data: any }>(
      `/api/funding/opportunities/${id}`
    );
  }

  async createFundingOpportunity(data: any) {
    return this.request<{ success: boolean; data: any; message?: string }>(
      "/api/funding/opportunities",
      {
        method: "POST",
        body: data,
      }
    );
  }

  async updateFundingOpportunity(id: string, data: any) {
    return this.request<{ success: boolean; data: any; message?: string }>(
      `/api/funding/opportunities/${id}`,
      {
        method: "PUT",
        body: data,
      }
    );
  }

  async deleteFundingOpportunity(id: string) {
    return this.request<{ success: boolean; message?: string }>(
      `/api/funding/opportunities/${id}`,
      {
        method: "DELETE",
      }
    );
  }

  async getFundingStats() {
    return this.request<{ success: boolean; data: any }>("/api/funding/stats");
  }

  async getOrganizationFunding() {
    return this.request<{
      success: boolean;
      data: { opportunities: any[]; total: number };
    }>("/api/funding/organization");
  }

  // Activity Reports
  async getActivityReport(params?: {
    period?: string;
    startDate?: string;
    endDate?: string;
  }) {
    const searchParams = new URLSearchParams();
    if (params?.period) searchParams.append("period", params.period);
    if (params?.startDate) searchParams.append("startDate", params.startDate);
    if (params?.endDate) searchParams.append("endDate", params.endDate);
    const query = searchParams.toString() ? `?${searchParams.toString()}` : "";
    return this.request<{ success: boolean; data: any }>(
      `/api/reports/activity${query}`
    );
  }
}

export const api = new ApiClient();
